import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, ChevronRight, Save, Send } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Skeleton } from '@/components/ui/Skeleton'
import { formatTimeAgo } from '@/lib/format'
import { CountsCard } from './components/CountsCard'
import {
  useAutosaveDraft,
  useSaveDraft,
  useShiftSummary,
  useSubmitHandover,
} from './hooks'
import type { AttachedRef } from './types'

/**
 * AF-135 — end-of-shift handover form.
 *
 * Layout:
 *   Header: shift identity + auto-save indicator.
 *   Counts: read-only auto-populated card.
 *   Form: notes, pending actions, escalations, attached ids.
 *   Footer: "Save draft" + "Submit & notify incoming shift".
 *
 * UX rules per AC + technical notes:
 *   - Auto-save every 30s (handled by `useAutosaveDraft`).
 *   - Submission is opt-in; the form makes clear that skipping is logged
 *     so analysts feel the social pressure without being blocked.
 *   - Counts come from the server and aren't editable.
 */
export function EndShiftPage() {
  const summary = useShiftSummary()
  const draft = summary.data?.active_draft ?? null

  if (summary.isLoading) return <Skeleton className="h-96 w-full" />
  if (summary.isError || !summary.data) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-muted-fg">Couldn't load shift data.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Editor
      key={draft?.id ?? `new-${summary.data.shift_id}`}
      shiftId={summary.data.shift_id}
      counts={summary.data.counts}
      initialDraft={draft}
    />
  )
}

interface EditorProps {
  shiftId: string
  counts: ReturnType<typeof useShiftSummary>['data'] extends infer T
    ? T extends { counts: infer C }
      ? C
      : never
    : never
  initialDraft: ReturnType<typeof useShiftSummary>['data'] extends infer T
    ? T extends { active_draft: infer D }
      ? D
      : never
    : never
}

function Editor({ shiftId, counts, initialDraft }: EditorProps) {
  const navigate = useNavigate()
  const [notes, setNotes] = useState(initialDraft?.handover_notes ?? '')
  const [pending, setPending] = useState(initialDraft?.pending_actions ?? '')
  const [escalations, setEscalations] = useState(initialDraft?.escalations ?? '')
  const [attachIdInput, setAttachIdInput] = useState('')
  const [attachments, setAttachments] = useState<AttachedRef[]>(
    initialDraft?.attachments ?? [],
  )

  const manualSave = useSaveDraft()
  const submit = useSubmitHandover()

  const autosave = useAutosaveDraft({
    shift_id: shiftId,
    draft: initialDraft,
    notes,
    pending,
    escalations,
    attachments,
    enabled: true,
  })

  // Track the currently-known server draft id. Once the autosave creates
  // one (or the manual Save returns one), submit can fire against it.
  const draftId = useMemo(
    () =>
      manualSave.data?.handover.id ??
      autosave.data?.handover.id ??
      initialDraft?.id ??
      null,
    [manualSave.data, autosave.data, initialDraft?.id],
  )

  // Saved-at hint reads from initialDraft.audit (which the parent
  // refreshes after every save) so the indicator survives the
  // key-driven Editor remount that happens when a new draft id appears.
  const lastSavedAt = useMemo(() => {
    const fromInitial = initialDraft?.audit
      .filter((a) => a.action === 'autosaved' || a.action === 'created')
      .map((a) => a.at)
      .pop()
    const fromMutations =
      manualSave.data?.handover.audit
        .filter((a) => a.action === 'autosaved' || a.action === 'created')
        .map((a) => a.at)
        .pop() ??
      autosave.data?.handover.audit
        .filter((a) => a.action === 'autosaved' || a.action === 'created')
        .map((a) => a.at)
        .pop()
    return fromMutations ?? fromInitial ?? null
  }, [initialDraft, manualSave.data, autosave.data])

  const submitDisabled =
    submit.isPending ||
    notes.trim().length === 0 ||
    pending.trim().length === 0

  const onSaveNow = () =>
    manualSave.mutate({
      id: draftId,
      shift_id: shiftId,
      handover_notes: notes,
      pending_actions: pending,
      escalations,
      attachments,
    })

  const onSubmit = () => {
    const doSubmit = (id: string) =>
      submit.mutate(id, {
        onSuccess: ({ handover }) =>
          void navigate(`/handovers/${handover.id}`),
      })
    if (draftId) {
      doSubmit(draftId)
      return
    }
    // No draft yet — save first, then submit on the resulting id.
    manualSave.mutate(
      {
        id: null,
        shift_id: shiftId,
        handover_notes: notes,
        pending_actions: pending,
        escalations,
        attachments,
      },
      { onSuccess: ({ handover }) => doSubmit(handover.id) },
    )
  }

  const onAddAttachment = () => {
    const raw = attachIdInput.trim()
    if (!raw) return
    const type: AttachedRef['type'] = raw.startsWith('case')
      ? 'case'
      : raw.startsWith('blk') || raw.startsWith('B-')
        ? 'block'
        : 'alert'
    setAttachments((cur) => [...cur, { type, id: raw, label: raw }])
    setAttachIdInput('')
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs />
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">End-of-shift handover</h1>
        <p className="text-sm text-muted-fg">
          Hand off context and pending work to the incoming analyst. Form
          autosaves every 30 seconds.{' '}
          <strong className="text-fg">
            Skipping a handover is recorded in the audit log.
          </strong>
        </p>
        {lastSavedAt && (
          <p className="text-xs text-status-success">
            Saved {formatTimeAgo(lastSavedAt)}
          </p>
        )}
      </header>

      <CountsCard counts={counts} />

      <Card>
        <CardHeader>
          <CardTitle>Handover notes</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="ho-notes">
              Notes <span className="text-muted-fg">(required)</span>
            </Label>
            <textarea
              id="ho-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did this shift work on? Where are the open threads?"
              className="min-h-[140px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ho-pending">
              Pending actions <span className="text-muted-fg">(required)</span>
            </Label>
            <textarea
              id="ho-pending"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
              placeholder="Numbered list of things the next analyst needs to do."
              className="min-h-[100px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ho-escalations">Escalations</Label>
            <textarea
              id="ho-escalations"
              value={escalations}
              onChange={(e) => setEscalations(e.target.value)}
              placeholder="Anything management or compliance flagged for follow-up."
              className="min-h-[80px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Attached items</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-xs text-muted-fg">
            Reference alerts, cases, or blocks the next analyst should look
            at. Paste an ID and press Add.
          </p>
          <div className="flex flex-wrap gap-2">
            <Input
              aria-label="Attach ID"
              value={attachIdInput}
              onChange={(e) => setAttachIdInput(e.target.value)}
              placeholder="alt-123 / case-456 / B-2026-0001"
              className="h-8 w-72"
            />
            <Button size="sm" variant="outline" onClick={onAddAttachment}>
              Add
            </Button>
          </div>
          {attachments.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {attachments.map((a) => (
                <li
                  key={a.id}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-xs"
                >
                  <span className="text-muted-fg uppercase">{a.type}</span>
                  <span className="font-mono">{a.label}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${a.label}`}
                    onClick={() =>
                      setAttachments((cur) => cur.filter((x) => x.id !== a.id))
                    }
                    className="ml-1 rounded text-muted-fg hover:text-fg"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onSaveNow}
          disabled={manualSave.isPending}
        >
          <Save size={14} aria-hidden /> Save draft
        </Button>
        <Button onClick={onSubmit} disabled={submitDisabled}>
          <Send size={14} aria-hidden /> {submit.isPending ? 'Submitting…' : 'Submit & notify'}
        </Button>
      </div>

      {submitDisabled && (
        <p className="flex items-center gap-2 text-xs text-muted-fg">
          <AlertTriangle size={12} aria-hidden /> Notes and pending actions are
          required before you can submit.
        </p>
      )}
    </div>
  )
}

function Breadcrumbs() {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-fg">
      <Link to="/handovers" className="rounded px-1 py-0.5 hover:bg-muted hover:text-fg">
        Handovers
      </Link>
      <ChevronRight size={12} aria-hidden />
      <span aria-current="page" className="text-fg">
        End shift
      </span>
    </nav>
  )
}
